import React from 'react';
import PropTypes from 'prop-types';

// ============================================================================
// Atomic Subcomponents
// ============================================================================

/**
 * InvoiceDetailRow - Reusable detail row with emoji, label, and value
 * Displays a single invoice detail in the format: emoji label: value
 * @param {string} emoji - Emoji icon to display
 * @param {string} label - Label text for the detail
 * @param {string} value - Value to display (bold)
 */
export const InvoiceDetailRow = ({ emoji, label, value }) => (
    <div style={{ marginBottom: '6px' }}>
        <span style={{ marginRight: '8px' }}>{emoji}</span>
        {label}: <span style={{ fontWeight: '600' }}>{value}</span>
    </div>
);

InvoiceDetailRow.propTypes = {
    emoji: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
};

/**
 * MessageFooterText - Footer text section with email and company name
 * Displays contact email and company signature
 */
export const MessageFooterText = () => (
    <>
        <div style={{ marginBottom: '8px' }}>For queries: billing@3plvision.com</div>
        <div>3PLVision Team</div>
    </>
);

// ============================================================================
// Composite Subcomponents (built from atomic components)
// ============================================================================

/**
 * InvoiceDetailsSection - Displays invoice details using multiple InvoiceDetailRow components
 * Shows: Invoice #, Invoice Date, Due Date, Amount Due
 * @param {Object} invoice - Invoice object with invoiceNumber, invoiceDate, dueDate, amount
 */
export const InvoiceDetailsSection = ({ invoice }) => (
    <div style={{ marginBottom: '10px', fontWeight: '500' }}>
        <InvoiceDetailRow 
            emoji="📋" 
            label="Invoice" 
            value={invoice.invoiceNumber}
        />
        <InvoiceDetailRow 
            emoji="📅" 
            label="Date" 
            value={invoice.invoiceDate}
        />
        <InvoiceDetailRow 
            emoji="⏰" 
            label="Due" 
            value={invoice.dueDate}
        />
        <InvoiceDetailRow 
            emoji="💰" 
            label="Amount Due" 
            value={`$${Math.abs(invoice.amount).toFixed(2)}`}
        />
    </div>
);

InvoiceDetailsSection.propTypes = {
    invoice: PropTypes.shape({
        invoiceNumber: PropTypes.string.isRequired,
        invoiceDate: PropTypes.string.isRequired,
        dueDate: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
    }).isRequired,
};

/**
 * DownloadLinkSection - Displays download link with URL for invoice PDF
 * Shows formatted portal URL for invoice download
 * @param {string} invoiceNumber - Invoice number for URL formatting
 */
export const DownloadLinkSection = ({ invoiceNumber }) => (
    <>
        <div style={{ marginBottom: '12px' }}>Download your invoice:</div>
        <div style={{ 
            marginBottom: '12px', 
            color: '#0066cc',
            textDecoration: 'underline',
            wordBreak: 'break-all',
            fontSize: '12px'
        }}>
            https://portal.3plvision.com/invoices/{invoiceNumber}.pdf
        </div>
    </>
);

DownloadLinkSection.propTypes = {
    invoiceNumber: PropTypes.string.isRequired,
};

/**
 * PopupHeader - Header section with WhatsApp icon, title, and close button
 * Displays the popup title and close button in header bar
 * @param {Function} onClose - Callback function when close button is clicked
 */
export const PopupHeader = ({ onClose }) => (
    <div data-testid="whatsapp-popup-header" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid #eee',
        backgroundColor: '#f9f9f9'
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fa fa-whatsapp" style={{ color: '#25d366', fontSize: '18px' }}></i>
            <span style={{ fontWeight: '600', fontSize: '13px', color: '#333' }}>WhatsApp Message Preview</span>
        </div>
        <button data-testid="whatsapp-close-btn" onClick={onClose} style={{
            background: 'none',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            color: '#666',
            padding: '0',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            ✕
        </button>
    </div>
);

PopupHeader.propTypes = {
    onClose: PropTypes.func.isRequired,
};

/**
 * PopupActionButtons - Action buttons section (Open in WhatsApp, Cancel)
 * Displays primary action button and secondary cancel button
 * @param {Object} invoice - Invoice object to pass to onOpen handler
 * @param {Function} onClose - Callback function when cancel button is clicked
 * @param {Function} onOpen - Callback function when "Open in WhatsApp" button is clicked
 */
export const PopupActionButtons = ({ invoice, onClose, onOpen }) => (
    <div data-testid="whatsapp-action-buttons" style={{
        display: 'flex',
        gap: '8px',
        padding: '12px 16px',
        borderTop: '1px solid #eee',
        backgroundColor: '#f9f9f9'
    }}>
        <button data-testid="whatsapp-open-btn" onClick={() => onOpen(invoice)} style={{
            flex: 1,
            padding: '8px 12px',
            backgroundColor: '#0066cc',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer'
        }}>
            Open in WhatsApp
        </button>
        <button data-testid="whatsapp-cancel-btn" onClick={onClose} style={{
            flex: 1,
            padding: '8px 12px',
            backgroundColor: '#fff',
            color: '#333',
            border: '1px solid #d0d0d0',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer'
        }}>
            Cancel
        </button>
    </div>
);

PopupActionButtons.propTypes = {
    invoice: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onOpen: PropTypes.func.isRequired,
};

// ============================================================================
// Complex Composite Subcomponent
// ============================================================================

/**
 * MessagePreviewContent - Main message body with green background
 * Composes multiple subcomponents: InvoiceDetailsSection, DownloadLinkSection, MessageFooterText
 * Shows the full WhatsApp message preview with all details
 * @param {Object} invoice - Invoice object with all required fields
 */
export const MessagePreviewContent = ({ invoice }) => (
    <div data-testid="whatsapp-message-content" style={{
        backgroundColor: '#e8f5e9',
        padding: '16px',
        maxHeight: '280px',
        overflowY: 'auto',
        fontSize: '13px',
        lineHeight: '1.6',
        color: '#1f2937'
    }}>
        <div style={{ marginBottom: '12px' }}>Hello {invoice.companyName},</div>
        <div style={{ marginBottom: '12px' }}>Your invoice from 3PLVision WMS is ready:</div>
        <div data-testid="whatsapp-invoice-details">
            <InvoiceDetailsSection invoice={invoice} />
        </div>
        <div data-testid="whatsapp-download-link">
            <DownloadLinkSection invoiceNumber={invoice.invoiceNumber} />
        </div>
        <MessageFooterText />
    </div>
);

MessagePreviewContent.propTypes = {
    invoice: PropTypes.shape({
        companyName: PropTypes.string.isRequired,
        invoiceNumber: PropTypes.string.isRequired,
        invoiceDate: PropTypes.string.isRequired,
        dueDate: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
    }).isRequired,
};
